import { ssoBaseUrl } from "./auth";
import {
  tenantMappingFor,
  toIdpPayload,
} from "./idpPresets";

/**
 * Get the admin API key.
 */
function adminKey() {
  const key = process.env.REACT_APP_SSO_ADMIN_API_KEY;
  if (!key) {
    throw new Error(
      "IdP admin API is not available from this build. Admin calls require an authenticated SSO admin session."
    );
  }
  return key;
}

/**
 * Common headers for admin API requests.
 */
function adminHeaders() {
  return {
    "Content-Type": "application/json",
    "X-API-Key": adminKey(),
  };
}

/**
 * Read and format API error response.
 */
async function readError(response) {
  const text = await response.text();

  try {
    const json = JSON.parse(text);

    return (
      json.detail ||
      json.message ||
      json.error ||
      json.title ||
      text ||
      `HTTP ${response.status}`
    );
  } catch {
    return text || `HTTP ${response.status}`;
  }
}

/**
 * Get all IdP configurations.
 */
export async function listIdpConfigs() {
  const response = await fetch(
    `${ssoBaseUrl()}/api/v1/idp-configs`,
    {
      headers: adminHeaders(),
    }
  );

  if (!response.ok) {
    throw new Error(await readError(response));
  }

  return await response.json();
}

/**
 * Create a new IdP configuration.
 */
export async function createIdpConfig(form) {
  const response = await fetch(
    `${ssoBaseUrl()}/api/v1/idp-configs`,
    {
      method: "POST",
      headers: adminHeaders(),
      body: JSON.stringify(toIdpPayload(form)),
    }
  );

  if (!response.ok) {
    throw new Error(await readError(response));
  }

  return await response.json();
}

/**
 * Update an existing IdP configuration.
 */
export async function updateIdpConfig(id, form) {
  const response = await fetch(
    `${ssoBaseUrl()}/api/v1/idp-configs/${id}`,
    {
      method: "PUT",
      headers: adminHeaders(),
      body: JSON.stringify(toIdpPayload(form)),
    }
  );

  if (!response.ok) {
    throw new Error(await readError(response));
  }

  return await response.json();
}

/**
 * Create when tenantKey is new.
 * Update when tenantKey already exists.
 */
export async function upsertIdpConfig(form) {
  const existing = (
    await listIdpConfigs()
  ).find(
    (item) =>
      item.tenantKey === form.tenantKey.trim()
  );

  if (existing) {
    return {
      saved: await updateIdpConfig(
        existing.id,
        form
      ),
      created: false,
    };
  }

  return {
    saved: await createIdpConfig(form),
    created: true,
  };
}

/**
 * Delete one IdP configuration.
 */
export async function deleteIdpConfig(id) {
  const response = await fetch(
    `${ssoBaseUrl()}/api/v1/idp-configs/${id}`,
    {
      method: "DELETE",
      headers: adminHeaders(),
    }
  );

  if (
    !response.ok &&
    response.status !== 204
  ) {
    throw new Error(await readError(response));
  }
}

/**
 * Delete all IdP configurations.
 */
export async function deleteAllIdpConfigs() {
  const existing = await listIdpConfigs();

  for (const item of existing) {
    await deleteIdpConfig(item.id);
  }

  return existing.length;
}

/**
 * Get all tenant mappings.
 */
export async function listTenantMappings() {
  const response = await fetch(
    `${ssoBaseUrl()}/api/v1/tenant-mappings`,
    {
      headers: adminHeaders(),
    }
  );

  if (!response.ok) {
    throw new Error(await readError(response));
  }

  return await response.json();
}

/**
 * Ensure the "acme" organization is mapped
 * to the selected IdP.
 *
 * Creates the mapping if it does not exist.
 * Updates the mapping if it already exists.
 */
export async function ensureTenantMappingForIdp(
  form
) {
  const desired = tenantMappingFor(form);

  const existing = (
    await listTenantMappings()
  ).find(
    (mapping) =>
      mapping.organizationId ===
      desired.organizationId
  );

  /**
   * No existing mapping.
   * Create a new one.
   */
  if (!existing) {
    const response = await fetch(
      `${ssoBaseUrl()}/api/v1/tenant-mappings`,
      {
        method: "POST",
        headers: adminHeaders(),
        body: JSON.stringify(desired),
      }
    );

    if (!response.ok) {
      throw new Error(
        await readError(response)
      );
    }

    return await response.json();
  }

  /**
   * Existing mapping.
   * Update providers.
   */
  const providers = [
    ...(existing.providers || []),
  ];

  const index = providers.findIndex(
    (provider) =>
      provider.tenantKey ===
      desired.defaultTenantKey
  );

  const binding = desired.providers[0];

  if (index >= 0) {
    providers[index] = {
      ...providers[index],
      ...binding,
      primary: true,
    };
  } else {
    providers.push(binding);
  }

  /**
   * Make the selected tenant the primary provider.
   */
  const normalized = providers.map(
    (provider) => ({
      ...provider,
      primary:
        provider.tenantKey ===
        desired.defaultTenantKey,
    })
  );

  const response = await fetch(
    `${ssoBaseUrl()}/api/v1/tenant-mappings/${existing.id}`,
    {
      method: "PUT",
      headers: adminHeaders(),
      body: JSON.stringify({
        organizationId:
          existing.organizationId,

        organizationName:
          existing.organizationName ||
          desired.organizationName,

        emailDomains:
          existing.emailDomains &&
          existing.emailDomains.length
            ? existing.emailDomains
            : desired.emailDomains,

        defaultTenantKey:
          desired.defaultTenantKey,

        enabled:
          existing.enabled !== false,

        providers: normalized,
      }),
    }
  );

  if (!response.ok) {
    throw new Error(await readError(response));
  }

  return await response.json();
}

/**
 * Delete one tenant mapping.
 */
export async function deleteTenantMapping(
  id
) {
  const response = await fetch(
    `${ssoBaseUrl()}/api/v1/tenant-mappings/${id}`,
    {
      method: "DELETE",
      headers: adminHeaders(),
    }
  );

  if (
    !response.ok &&
    response.status !== 204
  ) {
    throw new Error(await readError(response));
  }
}

/**
 * Delete all tenant mappings.
 */
export async function deleteAllTenantMappings() {
  const existing =
    await listTenantMappings();

  for (const item of existing) {
    await deleteTenantMapping(item.id);
  }

  return existing.length;
}

